import type { FC } from 'react'
import { useCallback } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Button } from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import LoginIcon from '@mui/icons-material/Login'
import { useNavigate } from 'react-router-dom'

import { useAuthActions, useAuthStatus } from '@/features/user'

import { HOME_CONTENT } from '../defaults'

export const StickyCtaButton: FC = () => {
  const { isAuthenticated, isLoading } = useAuthStatus()
  const { login } = useAuthActions()
  const navigate = useNavigate()

  const handleSignIn = useCallback((): void => {
    login().catch(() => {})
  }, [login])

  const handleDashboardClick = useCallback((): void => {
    navigate('/hosts')
  }, [navigate])

  if (isLoading) return null

  return (
    <Box sx={stickyContainerSx}>
      {isAuthenticated ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<DashboardIcon />}
          onClick={handleDashboardClick}
          sx={stickyButtonSx}
        >
          {HOME_CONTENT.ctaDashboard}
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          startIcon={<LoginIcon />}
          onClick={handleSignIn}
          sx={stickyButtonSx}
        >
          {HOME_CONTENT.ctaSignIn}
        </Button>
      )}
    </Box>
  )
}

const stickyContainerSx: SxProps<Theme> = {
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: 1100,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes fadeSlideIn': {
      from: { opacity: 0, transform: 'translateY(-8px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animation: 'fadeSlideIn 300ms ease-out',
  },
}

const stickyButtonSx: SxProps<Theme> = {
  px: 2.5,
  py: 0.75,
  fontSize: '0.875rem',
  fontWeight: 500,
  borderRadius: 2,
  textTransform: 'none',
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
  '&:hover': {
    backgroundColor: 'primary.dark',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
  },
  transition: 'all 0.2s ease-in-out',
}
